import React, { useCallback, useEffect } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { Redirect, useLocation } from 'react-router-dom';

import * as api from '../../../../../api';
import * as logger from '../../../../../logger';

import { Uploading } from './components';
import { INITIAL_OCR_STATE, OcrState } from '../types';
import {
  uploadImage,
  uploadMergedPictures,
} from '../../../UploadDocumentPage/utils';
import { Side } from '../../../../../utils/enum';
import { useFormWithoutBackButton } from '../../../../BackButtonContext';

interface ProcessingInsurancePageProps extends WithTranslation {
  encounter: api.Encounter.Encounter;
  pictures: { [Side.Front]: Blob | null; [Side.Back]: Blob | null };
  ocrState: OcrState;
  setOcrState: React.Dispatch<React.SetStateAction<OcrState>>;
  sendToOCR: (
    uploadId: string,
    side: Side,
    payor?: string,
  ) => Promise<api.InsuranceCardOcrResult.InsuranceCardOcrResult | undefined>;
  setPatientInsuranceInformationId: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
  onComplete: (
    patientInsuranceInformationId?: string,
    ocrResults?: api.InsuranceCardOcrResult.InsuranceCardOcrResult,
  ) => Promise<void>;
  setPatientUploadId: React.Dispatch<React.SetStateAction<string | undefined>>;
}

const ProcessingInsurancePage = ({
  encounter,
  ocrState,
  pictures,
  sendToOCR,
  setOcrState,
  setPatientInsuranceInformationId,
  onComplete,
  setPatientUploadId,
  t,
}: ProcessingInsurancePageProps) => {
  const frontPicture = pictures[Side.Front];
  const backPicture = pictures[Side.Back];
  const location = useLocation();
  useFormWithoutBackButton();

  const getSignedUploadUrl =
    useCallback(async (): Promise<api.PatientUpload.SignedUploadURLResult> => {
      try {
        const response = await api.client.PatientUpload.getSignedUploadURL(
          encounter.id,
          api.PatientUpload.DocumentType.InsuranceCard,
          { primary: false },
        );

        setPatientUploadId(response.id);
        setPatientInsuranceInformationId(
          response.patientInsuranceInformationId,
        );
        return response;
      } catch (e) {
        throw new Error('An error occurred. Please try again.');
      }
    }, [encounter.id, setPatientInsuranceInformationId, setPatientUploadId]);

  const uploadPictures = useCallback(
    async ({
      patientInsuranceInformationId,
    }: api.PatientUpload.SignedUploadURLResult): Promise<
      api.InsuranceCardOcrResult.InsuranceCardOcrResult | undefined
    > => {
      let frontResult:
        | api.InsuranceCardOcrResult.InsuranceCardOcrResult
        | undefined;
      try {
        frontResult = await uploadImage({
          documentType: api.PatientUpload.DocumentType.OcrVerification,
          imageBlob: frontPicture as Blob,
          encounterId: encounter.id,
          onUploaded: (uploadId: string) => sendToOCR(uploadId, Side.Front),
          options: { patientInsuranceInformationId },
        });

        if (!frontResult) {
          throw new Error();
        }
      } catch (e) {
        throw new Error('There was an error processing your insurance cards');
      }

      await uploadImage({
        documentType: api.PatientUpload.DocumentType.OcrVerification,
        imageBlob: backPicture as Blob,
        encounterId: encounter.id,
        onUploaded: (uploadId: string) =>
          sendToOCR(uploadId, Side.Back, frontResult?.insuranceProviderName),
        options: { patientInsuranceInformationId },
      });

      return frontResult;
    },
    [encounter.id, frontPicture, backPicture, sendToOCR],
  );

  useEffect(() => {
    const startOcr = async () => {
      if (ocrState.started) {
        return;
      }
      setOcrState((prev) => ({ ...prev, started: true }));

      let response: api.PatientUpload.SignedUploadURLResult | undefined =
        undefined;
      let ocrResults:
        | api.InsuranceCardOcrResult.InsuranceCardOcrResult
        | undefined;
      try {
        response = await getSignedUploadUrl();
        ocrResults = await uploadPictures(response);
        const { url, id } = response;
        if (frontPicture && backPicture) {
          await uploadMergedPictures({
            frontPicture,
            backPicture,
            url,
            id,
            encounter,
            onUploadProgress: () => null,
            onError: (errorMsg: string) => {
              setOcrState((prev) => ({ ...prev, error: errorMsg }));
              logger.error(
                new Error(
                  `Unexpected error uploading insurance card for encounter ${encounter.id} to ${url}`,
                ),
              );
            },
          });
        }
      } catch (e) {
        setOcrState((prev) => ({ ...prev, error: e.message }));
      }

      await onComplete(response?.patientInsuranceInformationId, ocrResults);
    };

    startOcr();
  }, [
    frontPicture,
    backPicture,
    encounter,
    getSignedUploadUrl,
    ocrState.started,
    onComplete,
    setOcrState,
    uploadPictures,
  ]);

  if (!pictures[Side.Front]) {
    setOcrState(INITIAL_OCR_STATE);
    return <Redirect to={'./' + location.search} />;
  }

  return <Uploading message={t('processingCard')} />;
};

export default withTranslation('processing')(ProcessingInsurancePage);
